import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { debounce } from "lodash";
import {
  ArrowLeft, Search, Plus, Trash2, Eye, EyeOff, Archive, RotateCcw,
  ChevronDown, ChevronRight, Save, Loader2, ImagePlus, GripVertical,
  Check, X, Filter, Package, AlertCircle, DollarSign, Upload, Grid3X3, Sparkles
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import FocalPointPicker from "../components/FocalPointPicker";
import VariationImageInput from "../components/VariationImageInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounced auto-save hook
function useAutoSave(productId, field, value, delay = 800) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const debouncedSave = useCallback(
    debounce(async (id, fieldName, fieldValue) => {
      if (!id) return;
      setSaving(true);
      try {
        const token = localStorage.getItem("adminToken");
        await axios.patch(`${API}/outfits/${id}/inline`, 
          { [fieldName]: fieldValue },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch (err) {
        toast.error(`Failed to save ${fieldName}`);
      } finally {
        setSaving(false);
      }
    }, delay),
    []
  );
  
  return { saving, saved, triggerSave: debouncedSave };
}

// Inline editable cell component
function EditableCell({ productId, field, value, type = "text", className = "", placeholder = "", onLocalChange }) {
  const [localValue, setLocalValue] = useState(value);
  const { saving, saved, triggerSave } = useAutoSave(productId, field, localValue);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    let newValue = e.target.value;
    if (type === "number") {
      newValue = newValue === "" ? 0 : parseFloat(newValue);
    }
    setLocalValue(newValue);
    if (onLocalChange) onLocalChange(field, newValue);
    triggerSave(productId, field, newValue);
  };
  
  return (
    <div className="relative group">
      <input
        type={type}
        value={localValue ?? ""}
        onChange={handleChange}
        placeholder={placeholder}
        className={`w-full bg-transparent border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none transition-colors py-1 px-1 text-[12px] ${className}`}
        data-testid={`edit-${field}-${productId}`}
      />
      {saving && <span className="absolute right-1 top-1/2 -translate-y-1/2"><Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" /></span>}
      {saved && <span className="absolute right-1 top-1/2 -translate-y-1/2"><Check className="w-3 h-3 text-green-500" /></span>}
    </div>
  );
}

// Category selection panel for expanded rows
//
// IMPORTANT — sync contract with /admin/outfits (Collection page) and the
// customer-facing /api/outfits?category=<name> filter:
//   • `categories`     stores parent  category NAMES (e.g. ["For Her"])
//   • `subcategories`  stores subcategory NAMES (e.g. ["Lehengas"])
//   • legacy `category` (singular) holds the subcategory NAME for back-compat
//
// We write names here so Collection's edit modal and the customer site can
// both read this product correctly. We read defensively (by NAME first, then
// fall back to ID) so any rows previously corrupted with IDs still render
// without re-saving them.
function CategoryPanel({ product, categories, onUpdate }) {
  const [saving, setSaving] = useState(false);

  // Get parent categories (top level)
  const parentCategories = categories.filter(c => !c.parent_id);

  // --- Resolve initial parent: accept NAME (canonical) or ID (legacy/corrupted) ---
  const initialParentRaw = product.categories?.[0] || "";
  const initialParent =
    parentCategories.find(c => c.name === initialParentRaw) ||
    parentCategories.find(c => c.id === initialParentRaw) ||
    null;
  const [localCategory, setLocalCategory] = useState(initialParent?.id || "");

  // --- Resolve initial subcategories: convert any IDs to NAMES for the UI ---
  const availableSubcategoriesForInit = initialParent?.children || [];
  const initialSubNames = (product.subcategories || []).map(s => {
    const byName = availableSubcategoriesForInit.find(sub => sub.name === s);
    if (byName) return byName.name;
    const byId = availableSubcategoriesForInit.find(sub => sub.id === s);
    return byId ? byId.name : s;          // keep raw value if it matches neither (don't lose data)
  });
  const [localSubcategoryNames, setLocalSubcategoryNames] = useState(initialSubNames);

  // Get subcategories based on currently-selected parent
  const selectedParent = categories.find(c => c.id === localCategory);
  const availableSubcategories = selectedParent?.children || [];

  const handleCategoryChange = async (newCategoryId) => {
    setLocalCategory(newCategoryId);
    // Clear subcategories when parent changes
    setLocalSubcategoryNames([]);

    const newParent = parentCategories.find(c => c.id === newCategoryId);
    const parentName = newParent?.name || "";

    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      // Write NAMES (matches Collection writer + customer API filter).
      // Also reset legacy `category` so the two singulars don't drift.
      const payload = {
        categories: parentName ? [parentName] : [],
        subcategories: [],
        category: "",
      };
      await axios.patch(`${API}/outfits/${product.id}/inline`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(payload);
      toast.success("Category updated");
    } catch {
      toast.error("Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const handleSubcategoryToggle = async (subId) => {
    const sub = availableSubcategories.find(s => s.id === subId);
    if (!sub) return;
    const subName = sub.name;
    const newSubNames = localSubcategoryNames.includes(subName)
      ? localSubcategoryNames.filter(s => s !== subName)
      : [...localSubcategoryNames, subName];
    setLocalSubcategoryNames(newSubNames);

    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      // Write NAMES. Mirror first subcategory name into legacy `category`
      // (singular) so AdminOutfits' fallback reader and any old code paths
      // that still read `category` keep working.
      const payload = {
        subcategories: newSubNames,
        category: newSubNames[0] || "",
      };
      await axios.patch(`${API}/outfits/${product.id}/inline`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(payload);
      toast.success("Subcategories updated");
    } catch {
      toast.error("Failed to update subcategories");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="bg-[#f9f7f2] border-t border-[#282828]/5 px-8 py-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Category Assignment</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
      </div>
      <div className="flex flex-wrap items-start gap-6">
        {/* Parent Category Dropdown */}
        <div>
          <label className="text-[9px] text-[#676664]/50 uppercase block mb-1">Parent Category</label>
          <select
            value={localCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="min-w-[160px] px-3 py-1.5 border border-[#282828]/10 text-[11px] bg-white focus:border-[#9A7B4F] focus:outline-none"
            data-testid={`category-select-${product.id}`}
          >
            <option value="">— Select Category —</option>
            {parentCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        
        {/* Subcategories (checkboxes) */}
        {availableSubcategories.length > 0 && (
          <div>
            <label className="text-[9px] text-[#676664]/50 uppercase block mb-1">Subcategories</label>
            <div className="flex flex-wrap gap-2">
              {availableSubcategories.map(sub => (
                <label 
                  key={sub.id} 
                  className={`flex items-center gap-1.5 px-2.5 py-1 border text-[11px] cursor-pointer transition-colors ${
                    localSubcategoryNames.includes(sub.name) 
                      ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' 
                      : 'bg-white text-[#676664] border-[#282828]/10 hover:border-[#9A7B4F]/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={localSubcategoryNames.includes(sub.name)}
                    onChange={() => handleSubcategoryToggle(sub.id)}
                    className="sr-only"
                  />
                  {sub.name}
                </label>
              ))}
            </div>
          </div>
        )}
        
        {/* Show current assignment */}
        <div className="ml-auto text-right">
          <span className="text-[9px] text-[#676664]/40 block">Currently assigned to:</span>
          <span className="text-[11px] text-[#676664]">
            {selectedParent?.name || "No category"} 
            {localSubcategoryNames.length > 0 && ` → ${localSubcategoryNames.join(", ")}`}
          </span>
        </div>
      </div>
    </div>
  );
}

// Event Types assignment panel for expanded rows
function EventTypesPanel({ product, eventTypes, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [localEventTypes, setLocalEventTypes] = useState(product.event_types || []);

  const handleToggle = async (etName) => {
    const newEts = localEventTypes.includes(etName)
      ? localEventTypes.filter(e => e !== etName)
      : [...localEventTypes, etName];
    setLocalEventTypes(newEts);
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${product.id}/inline`,
        { event_types: newEts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate({ event_types: newEts });
    } catch { toast.error("Failed to update event types"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#f9f7f2] border-t border-[#282828]/5 px-8 py-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Event Types</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
      </div>
      <div className="flex flex-wrap gap-2">
        {eventTypes.map(et => (
          <label
            key={et.id}
            className={`flex items-center gap-1.5 px-2.5 py-1 border text-[11px] cursor-pointer transition-colors ${
              localEventTypes.includes(et.name)
                ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                : 'bg-white text-[#676664] border-[#282828]/10 hover:border-[#9A7B4F]/50'
            }`}
            data-testid={`event-type-toggle-${et.id}-${product.id}`}
          >
            <input type="checkbox" checked={localEventTypes.includes(et.name)} onChange={() => handleToggle(et.name)} className="sr-only" />
            {et.name}
          </label>
        ))}
        {eventTypes.length === 0 && <span className="text-[11px] text-[#676664]/40">No event types configured</span>}
      </div>
    </div>
  );
}

// Curated "Complete the Look" panel for expanded rows
function CuratedSuggestionsPanel({ product, allProducts, productIndex, onUpdate, categories }) {
  const [saving, setSaving] = useState(false);
  const [curated, setCurated] = useState(product.curated_suggestions || []);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [addonType, setAddonType] = useState(product.addon_type || "individual");
  const [addonCategoryId, setAddonCategoryId] = useState(product.addon_category_id || "");
  const [showFullCollection, setShowFullCollection] = useState(product.addon_show_full_collection || false);
  const [dragIdx, setDragIdx] = useState(null);

  const otherProducts = allProducts.filter(p => p.id !== product.id && p.available !== false);
  const categoryProducts = addonCategoryId
    ? otherProducts.filter(p => (p.categories || []).includes(addonCategoryId) || p.category === addonCategoryId)
    : [];
  const searchSource = addonType === "category" ? categoryProducts : otherProducts;
  const filtered = searchTerm
    ? searchSource.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : searchSource;

  const save = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${product.id}/inline`, updates, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(updates);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveCurated = async (newList) => {
    setCurated(newList);
    save({ curated_suggestions: newList });
  };

  const addItem = (id) => {
    if (curated.includes(id) || curated.length >= 6) return;
    saveCurated([...curated, id]);
    setSearchTerm("");
  };
  const removeItem = (id) => saveCurated(curated.filter(c => c !== id));
  const getName = (id) => allProducts.find(p => p.id === id)?.name || productIndex?.[id]?.name || id.slice(0, 8);
  const getImage = (id) => allProducts.find(p => p.id === id)?.images?.[0] || productIndex?.[id]?.image;

  const handleTypeChange = (type) => {
    setAddonType(type);
    save({ addon_type: type });
  };

  const handleCategoryChange = (catId) => {
    setAddonCategoryId(catId);
    save({ addon_category_id: catId });
  };

  const handleFullCollectionToggle = () => {
    const val = !showFullCollection;
    setShowFullCollection(val);
    save({ addon_show_full_collection: val });
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); };
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const newList = [...curated];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(idx, 0, moved);
    setDragIdx(null);
    saveCurated(newList);
  };

  return (
    <div className="bg-[#f9f7f2] border-t border-[#282828]/5 px-8 py-3" data-testid={`curated-panel-${product.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Complete the Look — Configuration</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
      </div>

      {/* Add-On Type Selector */}
      <div className="flex gap-1.5 mb-3" data-testid={`addon-type-selector-${product.id}`}>
        {[
          { key: "individual", label: "Individual" },
          { key: "category", label: "Entire Category" },
          { key: "mixed", label: "Mixed" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => handleTypeChange(t.key)}
            className={`px-3 py-1.5 text-[10px] tracking-[0.05em] uppercase border transition-all ${
              addonType === t.key
                ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                : 'bg-white text-[#676664]/60 border-[#282828]/10 hover:border-[#9A7B4F]/30'
            }`}
            data-testid={`addon-type-${t.key}-${product.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Category Selector (for category/mixed modes) */}
      {(addonType === "category" || addonType === "mixed") && (
        <div className="mb-3" data-testid={`addon-category-select-${product.id}`}>
          <label className="text-[10px] text-[#676664]/40 uppercase tracking-wider block mb-1">Select Category</label>
          <select
            value={addonCategoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="w-full max-w-xs border border-[#282828]/12 px-3 py-1.5 text-[12px] bg-white text-[#282828] focus:border-[#9A7B4F] focus:outline-none"
          >
            <option value="">Choose a category...</option>
            {(categories || []).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* View Full Collection toggle */}
          <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid={`full-collection-toggle-${product.id}`}>
            <input
              type="checkbox"
              checked={showFullCollection}
              onChange={handleFullCollectionToggle}
              className="rounded border-[#282828]/20 w-3.5 h-3.5 text-[#9A7B4F] focus:ring-[#9A7B4F]"
            />
            <span className="text-[11px] text-[#676664]/50">Show "View Full Collection" button on storefront</span>
          </label>
        </div>
      )}

      {/* Curated items (drag-and-drop) */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] text-[#676664]/40">Featured Items ({curated.length}/6)</span>
        {curated.length < 6 && (
          <button onClick={() => setShowSearch(!showSearch)} className="text-[10px] text-[#9A7B4F] hover:text-[#8a6b3f]" data-testid={`toggle-curation-search-${product.id}`}>
            {showSearch ? "Close" : "+ Add"}
          </button>
        )}
      </div>

      {curated.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-2">
          {curated.map((id, idx) => (
            <div
              key={id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={() => handleDrop(idx)}
              className={`flex items-center gap-2 bg-white border border-[#282828]/10 px-2 py-1 cursor-grab active:cursor-grabbing ${dragIdx === idx ? 'opacity-40' : ''}`}
              data-testid={`curated-item-${id}`}
            >
              <span className="text-[9px] text-[#676664]/30 w-3">{idx + 1}</span>
              {getImage(id) && <img src={getImage(id)} alt="" className="w-6 h-6 object-cover" />}
              <span className="text-[11px] text-[#282828] max-w-[120px] truncate">{getName(id)}</span>
              <button onClick={() => removeItem(id)} className="text-[#676664]/30 hover:text-red-400 text-xs ml-1">&times;</button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-[#676664]/40 mb-2">No items selected — add up to 6 featured items</p>
      )}

      {/* Search to add */}
      {showSearch && (
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={addonType === "category" && addonCategoryId ? "Search within category..." : "Search products..."}
            className="w-full border border-[#282828]/10 px-3 py-1.5 text-[12px] bg-white"
            autoFocus
            data-testid={`curation-search-input-${product.id}`}
          />
          {searchTerm && (
            <div className="absolute z-20 top-full left-0 right-0 bg-white border border-[#282828]/10 shadow-lg max-h-48 overflow-y-auto">
              {filtered.slice(0, 8).map(p => (
                <button
                  key={p.id}
                  onClick={() => addItem(p.id)}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-[#FAF8F0] text-[12px] ${curated.includes(p.id) ? 'opacity-40' : ''}`}
                  disabled={curated.includes(p.id) || curated.length >= 6}
                  data-testid={`curation-result-${p.id}`}
                >
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-8 h-10 object-cover flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-[#282828] truncate">{p.name}</p>
                    <p className="text-[10px] text-[#676664]/50">{p.category} · ${p.price_4_day || 0}</p>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <p className="px-3 py-2 text-[11px] text-[#676664]/40">No results</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Item Type Panel (Main Item / Add-On checkboxes + discount %)
function ItemTypePanel({ product, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [isMain, setIsMain] = useState(product.is_main_item || false);
  const [isAddon, setIsAddon] = useState(product.is_addon || false);
  const [discountPct, setDiscountPct] = useState(product.addon_discount_percent || 0);

  const save = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${product.id}/inline`, updates, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(updates);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleMainToggle = () => {
    const val = !isMain;
    setIsMain(val);
    if (val && isAddon) { setIsAddon(false); save({ is_main_item: val, is_addon: false }); }
    else save({ is_main_item: val });
  };

  const handleAddonToggle = () => {
    const val = !isAddon;
    setIsAddon(val);
    if (val && isMain) { setIsMain(false); save({ is_addon: val, is_main_item: false }); }
    else save({ is_addon: val });
  };

  const handleDiscountChange = (e) => {
    const val = Math.max(0, Math.min(100, Number(e.target.value)));
    setDiscountPct(val);
  };

  const handleDiscountBlur = () => { save({ addon_discount_percent: discountPct }); };

  return (
    <div className="bg-[#f9f7f2] border-t border-[#282828]/5 px-8 py-3" data-testid={`item-type-panel-${product.id}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Item Type</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
      </div>
      <div className="flex items-center gap-6 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer" data-testid={`main-item-toggle-${product.id}`}>
          <input
            type="checkbox"
            checked={isMain}
            onChange={handleMainToggle}
            className="rounded border-[#282828]/20 w-4 h-4 text-[#9A7B4F] focus:ring-[#9A7B4F]"
          />
          <span className={`text-[12px] ${isMain ? 'text-[#282828] font-medium' : 'text-[#676664]/60'}`}>Main Item</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer" data-testid={`addon-toggle-${product.id}`}>
          <input
            type="checkbox"
            checked={isAddon}
            onChange={handleAddonToggle}
            className="rounded border-[#282828]/20 w-4 h-4 text-[#9A7B4F] focus:ring-[#9A7B4F]"
          />
          <span className={`text-[12px] ${isAddon ? 'text-[#282828] font-medium' : 'text-[#676664]/60'}`}>Add-On</span>
        </label>

        {isAddon && (
          <div className="flex items-center gap-2 ml-2" data-testid={`addon-discount-panel-${product.id}`}>
            <span className="text-[11px] text-[#676664]/50">Discount</span>
            <input
              type="number"
              min={0}
              max={100}
              value={discountPct}
              onChange={handleDiscountChange}
              onBlur={handleDiscountBlur}
              className="w-16 px-2 py-1 border border-[#282828]/15 text-[12px] text-center focus:border-[#9A7B4F] focus:outline-none"
              data-testid={`addon-discount-input-${product.id}`}
            />
            <span className="text-[11px] text-[#676664]/50">%</span>
          </div>
        )}
      </div>
    </div>
  );
}

// CTL visibility toggle for expanded rows
function CTLVisibilityToggle({ product, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(product.show_complete_the_look !== false);

  const handleToggle = async () => {
    const newValue = !enabled;
    setEnabled(newValue);
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${product.id}/inline`,
        { show_complete_the_look: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate({ show_complete_the_look: newValue });
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#f9f7f2] border-t border-[#282828]/5 px-8 py-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Complete the Look</span>
          {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid={`ctl-toggle-${product.id}`}>
          <span className={`text-[10px] ${enabled ? 'text-[#9A7B4F]' : 'text-[#676664]/40'}`}>{enabled ? 'Shown' : 'Hidden'}</span>
          <div onClick={handleToggle} className={`relative w-9 h-5 rounded-full transition-colors ${enabled ? 'bg-[#9A7B4F]' : 'bg-[#282828]/15'}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
        </label>
      </div>
    </div>
  );
}

// Product row component
function ProductRow({ product, categories, eventTypes, allProducts, productIndex, selected, onSelect, onToggleVisibility, onArchive, onDelete, onUpdate, expanded, onToggleExpand, onDuplicate }) {
  const [localProduct, setLocalProduct] = useState(product);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  useEffect(() => {
    setLocalProduct(product);
  }, [product]);
  
  const handleLocalChange = (field, value) => {
    setLocalProduct(prev => ({ ...prev, [field]: value }));
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    
    const token = localStorage.getItem("adminToken");
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const newImages = [...(localProduct.images || []), res.data.url];
      setLocalProduct(prev => ({ ...prev, images: newImages }));
      
      // Save to backend
      await axios.patch(`${API}/outfits/${product.id}/inline`, 
        { images: newImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };
  
  const removeImage = async (imgUrl) => {
    const token = localStorage.getItem("adminToken");
    const newImages = (localProduct.images || []).filter(img => img !== imgUrl);
    setLocalProduct(prev => ({ ...prev, images: newImages }));
    
    try {
      await axios.patch(`${API}/outfits/${product.id}/inline`, 
        { images: newImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Image removed");
    } catch {
      toast.error("Failed to remove image");
    }
  };
  
  const setPrimaryImage = async (imgUrl) => {
    const token = localStorage.getItem("adminToken");
    const otherImages = (localProduct.images || []).filter(img => img !== imgUrl);
    const newImages = [imgUrl, ...otherImages];
    setLocalProduct(prev => ({ ...prev, images: newImages }));
    
    try {
      await axios.patch(`${API}/outfits/${product.id}/inline`, 
        { images: newImages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Primary image set");
    } catch {
      toast.error("Failed to set primary image");
    }
  };
  
  const hasVariations = (localProduct.rental_options || []).length > 0;
  const primaryImage = (localProduct.images || [])[0];
  const missingData = !primaryImage || (!localProduct.price_4_day && !localProduct.price_8_day);
  
  return (
    <div className={`border-b border-[#282828]/5 ${localProduct.archived ? 'opacity-50 bg-[#f5f5f5]' : ''}`} data-testid={`product-row-${product.id}`}>
      {/* Main Row */}
      <div className="grid grid-cols-[30px_40px_70px_1fr_80px_80px_80px_80px_60px_70px_100px] gap-2 items-center px-3 py-2.5 hover:bg-[#FAF8F0]/50">
        {/* Checkbox for bulk selection */}
        <div className="flex justify-center">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(product.id)}
            className="rounded border-[#282828]/20 w-4 h-4"
            data-testid={`select-${product.id}`}
          />
        </div>
        
        {/* Expand Toggle - Always visible to access category/images/variations */}
        <button 
          onClick={() => onToggleExpand(product.id)} 
          className="text-[#676664]/30 hover:text-[#676664] flex justify-center"
          data-testid={`expand-${product.id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        
        {/* Image */}
        <div className="relative group">
          {primaryImage ? (
            <div className="w-14 h-14 bg-[#f7f5f4] border border-[#282828]/8 overflow-hidden">
              <img src={primaryImage.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${primaryImage}` : primaryImage} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-14 h-14 bg-[#f7f5f4] border border-dashed border-[#282828]/20 flex items-center justify-center">
              <ImagePlus className="w-4 h-4 text-[#676664]/30" />
            </div>
          )}
          <label className="absolute inset-0 cursor-pointer opacity-0 group-hover:opacity-100 bg-black/30 flex items-center justify-center transition-opacity">
            {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Upload className="w-4 h-4 text-white" />}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} />
          </label>
        </div>
        
        {/* Name & Description */}
        <div className="min-w-0">
          <EditableCell productId={product.id} field="name" value={localProduct.name} className="font-medium text-[#282828]" placeholder="Product name" onLocalChange={handleLocalChange} />
          <EditableCell productId={product.id} field="description" value={localProduct.description} className="text-[#676664]/60 text-[11px] truncate" placeholder="Description" onLocalChange={handleLocalChange} />
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[9px] text-[#676664]/40 uppercase">{localProduct.sku || 'No SKU'}</span>
            {/* Show current category inline */}
            {localProduct.categories?.[0] && (
              <span className="text-[9px] text-[#9A7B4F] bg-[#9A7B4F]/10 px-1.5 py-0.5 rounded">
                {categories.find(c => c.id === localProduct.categories[0])?.name || localProduct.categories[0]}
                {localProduct.subcategories?.length > 0 && ` → ${localProduct.subcategories.length} sub`}
              </span>
            )}
            {!localProduct.categories?.[0] && (
              <span className="text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">No category</span>
            )}
            {missingData && <AlertCircle className="w-3 h-3 text-amber-500" title="Missing data" />}
          </div>
        </div>
        
        {/* 4D Price */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">4 Day</span>
          <EditableCell productId={product.id} field="price_4_day" value={localProduct.price_4_day} type="number" className="text-center" onLocalChange={handleLocalChange} />
        </div>
        
        {/* 8D Price */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">8 Day</span>
          <EditableCell productId={product.id} field="price_8_day" value={localProduct.price_8_day} type="number" className="text-center" onLocalChange={handleLocalChange} />
        </div>
        
        {/* Bond */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">Bond</span>
          <EditableCell productId={product.id} field="bond_value" value={localProduct.bond_value} type="number" className="text-center" onLocalChange={handleLocalChange} />
        </div>
        
        {/* RRP */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">RRP</span>
          <EditableCell productId={product.id} field="rrp" value={localProduct.rrp || 0} type="number" className="text-center" onLocalChange={handleLocalChange} />
        </div>
        
        {/* Stock */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">Stock</span>
          <EditableCell productId={product.id} field="stock_quantity" value={localProduct.stock_quantity} type="number" className="text-center" onLocalChange={handleLocalChange} />
        </div>
        
        {/* Variations Count */}
        <div className="text-center">
          <span className="text-[9px] text-[#676664]/40 block mb-0.5">Variations</span>
          <span className="text-[12px] text-[#676664]">{(localProduct.rental_options || []).length}</span>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end gap-1">
          <button 
            onClick={() => onDuplicate(localProduct)}
            className="p-1.5 rounded transition-colors text-[#9A7B4F]/50 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/10"
            title="Duplicate listing"
            data-testid={`duplicate-${product.id}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2"/></svg>
          </button>
          <button 
            onClick={() => onToggleVisibility(product.id, !localProduct.available)}
            className={`p-1.5 rounded transition-colors ${localProduct.available ? 'text-[#4A7C59] hover:bg-[#4A7C59]/10' : 'text-[#676664]/30 hover:bg-[#676664]/10'}`}
            title={localProduct.available ? "Hide" : "Show"}
            data-testid={`visibility-${product.id}`}
          >
            {localProduct.available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button 
            onClick={() => onArchive(product.id)}
            className={`p-1.5 rounded transition-colors ${localProduct.archived ? 'text-amber-600 hover:bg-amber-50' : 'text-[#676664]/30 hover:bg-[#676664]/10'}`}
            title={localProduct.archived ? "Restore" : "Archive"}
            data-testid={`archive-${product.id}`}
          >
            {localProduct.archived ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      {/* Expanded Category Panel - FIRST so it's most prominent */}
      {expanded && (
        <CategoryPanel 
          product={localProduct} 
          categories={categories}
          onUpdate={(updates) => {
            setLocalProduct(prev => ({ ...prev, ...updates }));
          }}
        />
      )}

      {/* Expanded Event Types Panel */}
      {expanded && (
        <EventTypesPanel
          product={localProduct}
          eventTypes={eventTypes}
          onUpdate={(updates) => { setLocalProduct(prev => ({ ...prev, ...updates })); }}
        />
      )}

      {/* Expanded Curated Suggestions Panel */}
      {expanded && (
        <CuratedSuggestionsPanel
          product={localProduct}
          allProducts={allProducts}
          productIndex={productIndex}
          categories={categories}
          onUpdate={(updates) => { setLocalProduct(prev => ({ ...prev, ...updates })); }}
        />
      )}

      {/* CTL Visibility Toggle */}
      {expanded && (
        <CTLVisibilityToggle
          product={localProduct}
          onUpdate={(updates) => { setLocalProduct(prev => ({ ...prev, ...updates })); }}
        />
      )}

      {/* Item Type Panel (Main/Add-On) */}
      {expanded && (
        <ItemTypePanel
          product={localProduct}
          onUpdate={(updates) => { setLocalProduct(prev => ({ ...prev, ...updates })); }}
        />
      )}
      
      {/* Expanded Variations Panel */}
      {expanded && (
        <VariationsPanel product={localProduct} onUpdate={(variations) => {
          setLocalProduct(prev => ({ ...prev, rental_options: variations }));
        }} />
      )}
      
      {/* Expanded Images Panel */}
      {expanded && (
        <ImagesPanel 
          images={localProduct.images || []} 
          onRemove={removeImage} 
          onSetPrimary={setPrimaryImage}
          onUpload={handleImageUpload}
          uploading={uploadingImage}
          onAddFromLibrary={async (url) => {
            const newImages = [...(localProduct.images || []), url];
            setLocalProduct(prev => ({ ...prev, images: newImages }));
            try {
              const token = localStorage.getItem("adminToken");
              await axios.patch(`${API}/outfits/${product.id}/inline`, 
                { images: newImages },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success("Image added");
            } catch { toast.error("Failed to add image"); }
          }}
          onReorder={async (fromIdx, toIdx) => {
            if (fromIdx === toIdx) return;
            const imgs = [...(localProduct.images || [])];
            const [moved] = imgs.splice(fromIdx, 1);
            imgs.splice(toIdx, 0, moved);
            setLocalProduct(prev => ({ ...prev, images: imgs }));
            try {
              const token = localStorage.getItem("adminToken");
              await axios.patch(`${API}/outfits/${product.id}/inline`, 
                { images: imgs },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success("Image order saved");
            } catch { toast.error("Failed to save image order"); }
          }}
          onBatchUpload={async (files, onProgress) => {
            const token = localStorage.getItem("adminToken");
            const newUrls = [];
            for (let i = 0; i < files.length; i++) {
              try {
                const formData = new FormData();
                formData.append("file", files[i]);
                const res = await axios.post(`${API}/upload/image`, formData, {
                  headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                });
                newUrls.push(res.data.url);
              } catch { toast.error(`Failed to upload ${files[i].name}`); }
              if (onProgress) onProgress(i + 1);
            }
            if (newUrls.length > 0) {
              const updated = [...(localProduct.images || []), ...newUrls];
              setLocalProduct(prev => ({ ...prev, images: updated }));
              try {
                await axios.patch(`${API}/outfits/${product.id}/inline`,
                  { images: updated },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success(`Uploaded ${newUrls.length} image${newUrls.length > 1 ? 's' : ''}`);
              } catch { toast.error("Failed to save images"); }
            }
          }}
          focalPoints={localProduct.image_focal_points || {}}
          onFocalPointChange={async (imgUrl, x, y) => {
            const updated = { ...(localProduct.image_focal_points || {}), [imgUrl]: { x, y } };
            setLocalProduct(prev => ({ ...prev, image_focal_points: updated }));
            try {
              const token = localStorage.getItem("adminToken");
              await axios.patch(`${API}/outfits/${product.id}/inline`,
                { image_focal_points: updated },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch { toast.error("Failed to save focal point"); }
          }}
        />
      )}
    </div>
  );
}

// Variations panel for expanded rows
function VariationsPanel({ product, onUpdate }) {
  const [variations, setVariations] = useState(product.rental_options || []);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    setVariations(product.rental_options || []);
  }, [product.rental_options]);
  
  const saveVariations = async (newVariations) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${product.id}/inline`, 
        { rental_options: newVariations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(newVariations);
      toast.success("Variations saved");
    } catch {
      toast.error("Failed to save variations");
    } finally {
      setSaving(false);
    }
  };
  
  const updateVariation = (idx, field, value) => {
    const updated = [...variations];
    updated[idx] = { ...updated[idx], [field]: value };
    setVariations(updated);
  };
  
  const addVariation = () => {
    const newVar = {
      id: `var-${Date.now()}`,
      name: "New Variation",
      description: "",
      price_4_day: 0,
      price_8_day: 0,
      price_per_day: 0
    };
    setVariations([...variations, newVar]);
  };
  
  const removeVariation = (idx) => {
    const updated = variations.filter((_, i) => i !== idx);
    setVariations(updated);
    saveVariations(updated);
  };
  
  return (
    <div className="bg-[#FAF8F0]/70 border-t border-[#282828]/5 px-8 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">Rental Variations</span>
        <div className="flex gap-2">
          <button onClick={addVariation} className="text-[10px] text-[#9A7B4F] hover:underline flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add Variation
          </button>
          <button 
            onClick={() => saveVariations(variations)} 
            disabled={saving}
            className="text-[10px] text-[#282828] bg-white border border-[#282828]/10 px-2 py-1 hover:bg-[#f7f5f4] flex items-center gap-1"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {variations.map((v, idx) => (
          <div key={v.id || idx} className="grid grid-cols-[1fr_1fr_70px_70px_70px_88px_30px] gap-2 items-center bg-white border border-[#282828]/5 px-3 py-2">
            <input
              value={v.name}
              onChange={(e) => updateVariation(idx, "name", e.target.value)}
              placeholder="Name"
              className="text-[11px] border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1"
            />
            <input
              value={v.description}
              onChange={(e) => updateVariation(idx, "description", e.target.value)}
              placeholder="Description"
              className="text-[11px] text-[#676664]/60 border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1"
            />
            <div className="text-center">
              <span className="text-[8px] text-[#676664]/40 block">4D Price</span>
              <input
                type="number"
                value={v.price_4_day || 0}
                onChange={(e) => updateVariation(idx, "price_4_day", parseFloat(e.target.value) || 0)}
                className="w-full text-[11px] text-center border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1"
              />
            </div>
            <div className="text-center">
              <span className="text-[8px] text-[#676664]/40 block">8D Price</span>
              <input
                type="number"
                value={v.price_8_day || 0}
                onChange={(e) => updateVariation(idx, "price_8_day", parseFloat(e.target.value) || 0)}
                className="w-full text-[11px] text-center border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1"
              />
            </div>
            <div className="text-center" title="Leave 0 to use the product-level bond">
              <span className="text-[8px] text-[#676664]/40 block">Bond ($)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={v.bond_value ?? ""}
                onChange={(e) => updateVariation(idx, "bond_value", e.target.value === "" ? "" : (parseFloat(e.target.value) || 0))}
                placeholder={`${product.bond_value || 0}`}
                className="w-full text-[11px] text-center border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1"
              />
            </div>
            <div className="text-center" title="Variation image — upload from desktop or pick from library. Falls back to product images if empty.">
              <span className="text-[8px] text-[#676664]/40 block">Image</span>
              <VariationImageInput
                value={v.image || ""}
                onChange={(url) => updateVariation(idx, "image", url)}
                compact={true}
                testIdPrefix={`cat-variation-${idx}-`}
              />
            </div>
            <button onClick={() => removeVariation(idx)} className="text-[#676664]/20 hover:text-red-500 justify-self-center">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {variations.length === 0 && (
          <p className="text-[11px] text-[#676664]/40 text-center py-4">No variations. Click &quot;Add Variation&quot; to create one.</p>
        )}
      </div>
    </div>
  );
}

// Images panel for expanded rows
function ImagesPanel({ images, onRemove, onSetPrimary, onUpload, uploading, onAddFromLibrary, onReorder, onBatchUpload, focalPoints, onFocalPointChange }) {
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [reorderDragIdx, setReorderDragIdx] = useState(null);
  const [reorderOverIdx, setReorderOverIdx] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [selectedForFocal, setSelectedForFocal] = useState(null);

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    if (files.length && onBatchUpload) handleBatchUpload(files);
  };

  const handleFileZoneDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDropActive(true);
    else if (e.type === "dragleave") setDropActive(false);
  };

  const handleMultiFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length && onBatchUpload) handleBatchUpload(files);
    e.target.value = "";
  };

  const handleBatchUpload = async (files) => {
    setBatchUploading(true);
    setBatchProgress({ done: 0, total: files.length });
    await onBatchUpload(files, (done) => setBatchProgress({ done, total: files.length }));
    setBatchUploading(false);
  };
  
  const loadLibrary = async () => {
    setShowLibrary(true);
    setLoadingLibrary(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/media-library`, { headers: { Authorization: `Bearer ${token}` } });
      setLibraryImages(res.data);
    } catch { toast.error("Failed to load image library"); }
    finally { setLoadingLibrary(false); }
  };
  
  const addFromLibrary = (url) => {
    if (!images.includes(url)) {
      onAddFromLibrary(url);
      toast.success("Image added");
    }
  };

  return (
    <div className="bg-[#faf9f5] border-t border-[#282828]/5 px-8 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">
          Product Images {onReorder && <span className="normal-case text-[#9A7B4F] ml-1">— drag to reorder</span>}
        </span>
        <button onClick={loadLibrary} className="text-[10px] text-[#9A7B4F] hover:underline flex items-center gap-1">
          <Grid3X3 className="w-3 h-3" /> Browse Library
        </button>
      </div>

      {/* Batch upload drop zone */}
      {onBatchUpload && (
        <div
          onDragEnter={handleFileZoneDrag} onDragLeave={handleFileZoneDrag} onDragOver={handleFileZoneDrag} onDrop={handleFileDrop}
          className={`border border-dashed rounded p-3 mb-3 text-center transition-colors ${
            dropActive ? 'border-[#9A7B4F] bg-[#9A7B4F]/5' : 'border-[#282828]/15 hover:border-[#9A7B4F]/40'
          }`}
          data-testid="expanded-image-drop-zone"
        >
          {batchUploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" />
              <span className="text-[11px] text-[#9A7B4F]">Uploading {batchProgress.done}/{batchProgress.total}...</span>
            </div>
          ) : (
            <p className="text-[11px] text-[#676664]/50">
              Drop images here, or{" "}
              <label className="text-[#9A7B4F] cursor-pointer hover:underline">
                select multiple files
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleMultiFileSelect} data-testid="expanded-batch-file-input" />
              </label>
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {images.map((img, idx) => {
          const isDragging = reorderDragIdx === idx;
          const isOver = reorderOverIdx === idx;
          return (
            <div
              key={img}
              draggable={!!onReorder}
              onDragStart={onReorder ? (e) => { e.dataTransfer.effectAllowed = 'move'; setReorderDragIdx(idx); } : undefined}
              onDragOver={onReorder ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setReorderOverIdx(idx); } : undefined}
              onDragLeave={onReorder ? () => setReorderOverIdx(null) : undefined}
              onDrop={onReorder ? (e) => { e.preventDefault(); onReorder(reorderDragIdx, idx); setReorderDragIdx(null); setReorderOverIdx(null); } : undefined}
              onDragEnd={onReorder ? () => { setReorderDragIdx(null); setReorderOverIdx(null); } : undefined}
              className={`relative group w-20 h-20 border bg-white overflow-hidden ${onReorder ? 'cursor-grab active:cursor-grabbing' : ''} transition-all ${
                isDragging ? 'opacity-40 scale-95' : ''
              } ${isOver && !isDragging ? 'border-[#9A7B4F] ring-2 ring-[#9A7B4F]/30 scale-105' : 'border-[#282828]/10'}`}
            >
              <img 
                src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} 
                alt="" 
                className="w-full h-full object-cover pointer-events-none"
                style={{ objectPosition: `${(focalPoints || {})[img]?.x ?? 50}% ${(focalPoints || {})[img]?.y ?? 50}%` }}
              />
              {idx === 0 && <span className="absolute top-0.5 left-0.5 bg-[#9A7B4F] text-white text-[8px] px-1">Primary</span>}
              {selectedForFocal === img && <span className="absolute bottom-0.5 left-0.5 bg-[#9A7B4F] text-white text-[7px] px-1">Editing</span>}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {onReorder && <GripVertical className="w-3 h-3 text-white/60 absolute top-0.5 left-0.5" />}
                {onFocalPointChange && (
                  <button onClick={() => setSelectedForFocal(selectedForFocal === img ? null : img)} className="p-1 bg-white/90 rounded text-[#9A7B4F] hover:bg-white" title="Set focal point">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg>
                  </button>
                )}
                {idx !== 0 && (
                  <button onClick={() => onSetPrimary(img)} className="p-1 bg-white/90 rounded text-[#282828] hover:bg-white" title="Set as primary">
                    <Check className="w-3 h-3" />
                  </button>
                )}
                <button onClick={() => onRemove(img)} className="p-1 bg-white/90 rounded text-red-500 hover:bg-white" title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
        {/* URL paste input */}
        <PasteImageUrl onAdd={onAddFromLibrary} />
        {!onBatchUpload && (
          <label className="w-20 h-20 border border-dashed border-[#282828]/20 bg-white flex flex-col items-center justify-center cursor-pointer hover:border-[#9A7B4F] transition-colors">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" /> : <Upload className="w-4 h-4 text-[#676664]/30" />}
            <span className="text-[8px] text-[#676664]/40 mt-1">Upload</span>
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
          </label>
        )}
      </div>
      
      {/* Library Modal */}
      {showLibrary && (
        <div className="mt-3 border border-[#282828]/10 bg-white p-3 max-h-60 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-[#676664]/60 uppercase tracking-wider">Image Library</span>
            <button onClick={() => setShowLibrary(false)} className="text-[#676664]/40 hover:text-[#676664]"><X className="w-3 h-3" /></button>
          </div>
          {loadingLibrary ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
          ) : libraryImages.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {libraryImages.map((img) => {
                const url = img.url;
                const fullUrl = url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                const isAdded = images.includes(url) || images.includes(fullUrl);
                return (
                  <button 
                    key={img.id || url} 
                    onClick={() => addFromLibrary(fullUrl)}
                    disabled={isAdded}
                    className={`relative w-16 h-16 border overflow-hidden ${isAdded ? 'border-[#9A7B4F] opacity-50' : 'border-[#282828]/10 hover:border-[#9A7B4F]'}`}
                  >
                    <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                    {isAdded && <span className="absolute inset-0 flex items-center justify-center bg-white/60"><Check className="w-4 h-4 text-[#9A7B4F]" /></span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-[#676664]/40 text-center py-4">No images in library. Upload images from the Collection page first.</p>
          )}
        </div>
      )}

      {/* Focal Point Picker */}
      {selectedForFocal && onFocalPointChange && (
        <div className="mt-3" data-testid="focal-point-editor">
          <FocalPointPicker
            imageUrl={selectedForFocal.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${selectedForFocal}` : selectedForFocal}
            positionX={(focalPoints || {})[selectedForFocal]?.x ?? 50}
            positionY={(focalPoints || {})[selectedForFocal]?.y ?? 50}
            onChange={(x, y) => onFocalPointChange(selectedForFocal, x, y)}
          />
        </div>
      )}
    </div>
  );
}

// Paste image URL component
function PasteImageUrl({ onAdd }) {
  const [show, setShow] = useState(false);
  const [url, setUrl] = useState("");
  
  if (!show) {
    return (
      <button onClick={() => setShow(true)} className="w-20 h-20 border border-dashed border-[#282828]/20 bg-white flex flex-col items-center justify-center hover:border-[#9A7B4F] transition-colors" title="Paste URL">
        <ImagePlus className="w-4 h-4 text-[#676664]/30" />
        <span className="text-[8px] text-[#676664]/40 mt-1">Paste URL</span>
      </button>
    );
  }
  
  return (
    <div className="flex flex-col gap-1">
      <input 
        value={url} 
        onChange={(e) => setUrl(e.target.value)} 
        placeholder="https://..." 
        className="w-40 border border-[#282828]/10 px-2 py-1 text-[10px] focus:border-[#9A7B4F] focus:outline-none"
        autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter' && url.trim()) { onAdd(url.trim()); setUrl(""); setShow(false); } }}
      />
      <div className="flex gap-1">
        <button onClick={() => { if (url.trim()) { onAdd(url.trim()); setUrl(""); setShow(false); } }} className="text-[9px] text-[#9A7B4F] hover:underline">Add</button>
        <button onClick={() => { setUrl(""); setShow(false); }} className="text-[9px] text-[#676664]/40 hover:underline">Cancel</button>
      </div>
    </div>
  );
}

// New product modal form with images & variations
function NewProductForm({ categoryId, subcategoryId, categories, onSave, onCancel }) {
  // Resolve incoming IDs to NAMES so the new product is created with the
  // canonical category contract (names). See `CategoryPanel` header comment.
  const _parentObj = (categories || []).find(c => c.id === categoryId);
  const _subObj = (_parentObj?.children || []).find(s => s.id === subcategoryId);
  const _parentName = _parentObj?.name || "";
  const _subName = _subObj?.name || "";

  const [product, setProduct] = useState({
    name: "",
    description: "",
    price_4_day: 0,
    price_8_day: 0,
    bond_value: 0,
    rrp: 0,
    stock_quantity: 1,
    category: _subName,                            // legacy singular = subcategory name
    subcategories: _subName ? [_subName] : [],     // canonical = names
    categories: _parentName ? [_parentName] : [],  // canonical = names
    images: [],
    rental_options: [],
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  // Image management state
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Image reorder drag state
  const [reorderDragIdx, setReorderDragIdx] = useState(null);
  const [reorderOverIdx, setReorderOverIdx] = useState(null);

  // Variation state
  const [variations, setVariations] = useState([]);

  const handleImageReorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setProduct(prev => {
      const imgs = [...prev.images];
      const [moved] = imgs.splice(fromIdx, 1);
      imgs.splice(toIdx, 0, moved);
      return { ...prev, images: imgs };
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    if (files.length) uploadMultipleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length) uploadMultipleFiles(files);
    e.target.value = "";
  };

  const uploadMultipleFiles = async (files) => {
    setUploadingImages(true);
    setUploadProgress({ done: 0, total: files.length });
    const token = localStorage.getItem("adminToken");
    const newUrls = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append("file", files[i]);
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        newUrls.push(res.data.url);
      } catch {
        toast.error(`Failed to upload ${files[i].name}`);
      }
      setUploadProgress({ done: i + 1, total: files.length });
    }

    if (newUrls.length > 0) {
      setProduct(prev => ({ ...prev, images: [...prev.images, ...newUrls] }));
      toast.success(`Uploaded ${newUrls.length} image${newUrls.length > 1 ? 's' : ''}`);
    }
    setUploadingImages(false);
  };

  const addImageUrl = (url) => {
    if (url && !product.images.includes(url)) {
      setProduct(prev => ({ ...prev, images: [...prev.images, url] }));
    }
  };

  const removeImage = (imgUrl) => {
    setProduct(prev => ({ ...prev, images: prev.images.filter(i => i !== imgUrl) }));
  };

  const setPrimaryImage = (imgUrl) => {
    setProduct(prev => ({
      ...prev,
      images: [imgUrl, ...prev.images.filter(i => i !== imgUrl)],
    }));
  };

  const loadLibrary = async () => {
    setShowLibrary(true);
    setLoadingLibrary(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/media-library`, { headers: { Authorization: `Bearer ${token}` } });
      setLibraryImages(res.data);
    } catch { toast.error("Failed to load library"); }
    finally { setLoadingLibrary(false); }
  };

  // Variation handlers
  const addVariation = () => {
    setVariations(prev => [...prev, {
      id: `var-${Date.now()}`,
      name: "New Variation",
      description: "",
      price_4_day: 0,
      price_8_day: 0,
      price_per_day: 0,
    }]);
  };

  const updateVariation = (idx, field, value) => {
    setVariations(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeVariation = (idx) => {
    setVariations(prev => prev.filter((_, i) => i !== idx));
  };

  // Category helpers
  const parentCategories = (categories || []).filter(c => !c.parent_id);
  const selectedParent = (categories || []).find(c => c.id === product.categories?.[0]);
  const availableSubs = selectedParent?.children || [];

  const handleSave = async () => {
    if (!product.name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      const payload = { ...product, rental_options: variations.length > 0 ? variations : null };
      const res = await axios.post(`${API}/outfits`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product created");
      onSave(res.data);
    } catch { toast.error("Failed to create product"); }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: "details", label: "Details" },
    { id: "images", label: `Images (${product.images.length})` },
    { id: "variations", label: `Variations (${variations.length})` },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onCancel} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white z-50 w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-xl flex flex-col" data-testid="new-product-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#282828]/8">
          <h2 className="text-base text-[#282828] font-light">Add New Product</h2>
          <button onClick={onCancel} className="text-[#676664]/40 hover:text-[#676664]"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#282828]/8 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-[11px] uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#9A7B4F] text-[#9A7B4F] font-medium'
                  : 'border-transparent text-[#676664]/50 hover:text-[#676664]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Product Name *</label>
                <input value={product.name} onChange={e => setProduct(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Royal Blue Lehenga" autoFocus
                  className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="new-product-name" />
              </div>
              <div>
                <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Description</label>
                <textarea value={product.description} onChange={e => setProduct(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe the piece..."
                  rows={3}
                  className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] focus:border-[#9A7B4F] focus:outline-none resize-none" />
              </div>
              {/* Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Category</label>
                  <select value={product.categories?.[0] || ""}
                    onChange={e => setProduct(p => ({ ...p, categories: e.target.value ? [e.target.value] : [], subcategories: [] }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[12px] bg-white focus:border-[#9A7B4F] focus:outline-none">
                    <option value="">— Select —</option>
                    {parentCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {availableSubs.length > 0 && (
                  <div>
                    <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Subcategories</label>
                    <div className="flex flex-wrap gap-1.5">
                      {availableSubs.map(sub => (
                        <label key={sub.id} className={`flex items-center gap-1 px-2 py-1 border text-[10px] cursor-pointer transition-colors ${
                          (product.subcategories || []).includes(sub.id)
                            ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                            : 'bg-white text-[#676664] border-[#282828]/10 hover:border-[#9A7B4F]/50'
                        }`}>
                          <input type="checkbox" className="sr-only"
                            checked={(product.subcategories || []).includes(sub.id)}
                            onChange={() => setProduct(p => ({
                              ...p,
                              subcategories: (p.subcategories || []).includes(sub.id)
                                ? p.subcategories.filter(s => s !== sub.id)
                                : [...(p.subcategories || []), sub.id]
                            }))} />
                          {sub.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Pricing */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">4-Day Price</label>
                  <input type="number" value={product.price_4_day} onChange={e => setProduct(p => ({ ...p, price_4_day: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] text-center focus:border-[#9A7B4F] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">8-Day Price</label>
                  <input type="number" value={product.price_8_day} onChange={e => setProduct(p => ({ ...p, price_8_day: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] text-center focus:border-[#9A7B4F] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Bond</label>
                  <input type="number" value={product.bond_value} onChange={e => setProduct(p => ({ ...p, bond_value: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] text-center focus:border-[#9A7B4F] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">RRP</label>
                  <input type="number" value={product.rrp} onChange={e => setProduct(p => ({ ...p, rrp: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] text-center focus:border-[#9A7B4F] focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Stock</label>
                  <input type="number" value={product.stock_quantity} onChange={e => setProduct(p => ({ ...p, stock_quantity: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] text-center focus:border-[#9A7B4F] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Size</label>
                  <input value={product.size || ""} onChange={e => setProduct(p => ({ ...p, size: e.target.value }))}
                    placeholder="e.g. Free, S, M"
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] focus:border-[#9A7B4F] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Color</label>
                  <input value={product.color || ""} onChange={e => setProduct(p => ({ ...p, color: e.target.value }))}
                    placeholder="e.g. Red, Blue"
                    className="w-full border border-[#282828]/10 px-3 py-2 text-[13px] focus:border-[#9A7B4F] focus:outline-none" />
                </div>
              </div>
            </div>
          )}

          {/* Images Tab */}
          {activeTab === "images" && (
            <div className="space-y-4">
              {/* Drag & Drop Zone */}
              <div
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive ? 'border-[#9A7B4F] bg-[#9A7B4F]/5' : 'border-[#282828]/15 hover:border-[#9A7B4F]/50'
                }`}
                data-testid="image-drop-zone"
              >
                <Upload className="w-8 h-8 text-[#9A7B4F]/40 mx-auto mb-2" />
                <p className="text-[13px] text-[#676664]/70 mb-1">
                  Drag & drop images here, or{" "}
                  <label className="text-[#9A7B4F] cursor-pointer hover:underline">
                    browse files
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} data-testid="batch-file-input" />
                  </label>
                </p>
                <p className="text-[10px] text-[#676664]/40">Select multiple files for batch upload. JPG, PNG, WebP up to 10MB each.</p>
                {uploadingImages && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" />
                    <span className="text-[11px] text-[#9A7B4F]">Uploading {uploadProgress.done}/{uploadProgress.total}...</span>
                  </div>
                )}
              </div>

              {/* External URL Input */}
              <div className="flex gap-2">
                <input
                  value={urlInput} onChange={e => setUrlInput(e.target.value)}
                  placeholder="Paste external image URL (https://...)"
                  className="flex-1 border border-[#282828]/10 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none"
                  onKeyDown={e => { if (e.key === 'Enter' && urlInput.trim()) { addImageUrl(urlInput.trim()); setUrlInput(""); } }}
                  data-testid="external-url-input"
                />
                <Button size="sm" variant="outline" onClick={() => { if (urlInput.trim()) { addImageUrl(urlInput.trim()); setUrlInput(""); } }}
                  className="text-[11px] border-[#9A7B4F] text-[#9A7B4F]" data-testid="add-url-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add URL
                </Button>
                <Button size="sm" variant="outline" onClick={loadLibrary}
                  className="text-[11px]" data-testid="browse-library-btn">
                  <Grid3X3 className="w-3 h-3 mr-1" /> Library
                </Button>
              </div>

              {/* Current Images Grid - Drag to reorder */}
              {product.images.length > 0 && (
                <div>
                  <span className="text-[10px] text-[#676664]/60 uppercase block mb-2">Added Images ({product.images.length}) — <span className="normal-case text-[#9A7B4F]">drag to reorder</span></span>
                  <div className="flex flex-wrap gap-2">
                    {product.images.map((img, idx) => {
                      const src = img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
                      const isDragging = reorderDragIdx === idx;
                      const isOver = reorderOverIdx === idx;
                      return (
                        <div
                          key={img + idx}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setReorderDragIdx(idx); }}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setReorderOverIdx(idx); }}
                          onDragLeave={() => setReorderOverIdx(null)}
                          onDrop={(e) => { e.preventDefault(); handleImageReorder(reorderDragIdx, idx); setReorderDragIdx(null); setReorderOverIdx(null); }}
                          onDragEnd={() => { setReorderDragIdx(null); setReorderOverIdx(null); }}
                          className={`relative group w-24 h-24 border bg-white overflow-hidden cursor-grab active:cursor-grabbing transition-all ${
                            isDragging ? 'opacity-40 scale-95' : ''
                          } ${isOver && !isDragging ? 'border-[#9A7B4F] ring-2 ring-[#9A7B4F]/30 scale-105' : 'border-[#282828]/10'}`}
                          data-testid={`new-product-image-${idx}`}
                        >
                          <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" />
                          {idx === 0 && <span className="absolute top-0.5 left-0.5 bg-[#9A7B4F] text-white text-[8px] px-1">Primary</span>}
                          <div className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="bg-black/50 text-white text-[8px] px-1 rounded">{idx + 1}</span>
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            <GripVertical className="w-3 h-3 text-white/60 absolute top-1 left-1" />
                            {idx !== 0 && (
                              <button onClick={(e) => { e.stopPropagation(); setPrimaryImage(img); }} className="p-1 bg-white/90 rounded text-[#282828] hover:bg-white" title="Set primary">
                                <Check className="w-3 h-3" />
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); removeImage(img); }} className="p-1 bg-white/90 rounded text-red-500 hover:bg-white" title="Remove">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {product.images.length === 0 && !uploadingImages && (
                <p className="text-[11px] text-[#676664]/40 text-center py-4">No images added yet. Upload files, paste URLs, or browse your library.</p>
              )}

              {/* Library Modal */}
              {showLibrary && (
                <div className="border border-[#282828]/10 bg-[#faf9f5] p-3 max-h-48 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-[#676664]/60 uppercase tracking-wider">Media Library</span>
                    <button onClick={() => setShowLibrary(false)} className="text-[#676664]/40 hover:text-[#676664]"><X className="w-3 h-3" /></button>
                  </div>
                  {loadingLibrary ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
                  ) : libraryImages.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {libraryImages.map(item => {
                        const url = item.url;
                        const fullUrl = url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                        const isAdded = product.images.includes(url) || product.images.includes(fullUrl);
                        return (
                          <button key={item.id || url} onClick={() => { if (!isAdded) addImageUrl(fullUrl); }}
                            disabled={isAdded}
                            className={`relative w-16 h-16 border overflow-hidden ${isAdded ? 'border-[#9A7B4F] opacity-50' : 'border-[#282828]/10 hover:border-[#9A7B4F]'}`}>
                            <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                            {isAdded && <span className="absolute inset-0 flex items-center justify-center bg-white/60"><Check className="w-4 h-4 text-[#9A7B4F]" /></span>}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-[#676664]/40 text-center py-4">No images in library.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Variations Tab */}
          {activeTab === "variations" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[#676664]/60 uppercase">Rental Variations</span>
                <button onClick={addVariation} className="text-[10px] text-[#9A7B4F] hover:underline flex items-center gap-1" data-testid="add-variation-btn">
                  <Plus className="w-3 h-3" /> Add Variation
                </button>
              </div>
              {variations.map((v, idx) => (
                <div key={v.id} className="grid grid-cols-[1fr_1fr_90px_90px_30px] gap-2 items-center bg-[#faf9f5] border border-[#282828]/5 px-3 py-2">
                  <input value={v.name} onChange={e => updateVariation(idx, "name", e.target.value)} placeholder="Name"
                    className="text-[11px] border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1" />
                  <input value={v.description} onChange={e => updateVariation(idx, "description", e.target.value)} placeholder="Description"
                    className="text-[11px] text-[#676664]/60 border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1" />
                  <div className="text-center">
                    <span className="text-[8px] text-[#676664]/40 block">4D Price</span>
                    <input type="number" value={v.price_4_day || 0} onChange={e => updateVariation(idx, "price_4_day", parseFloat(e.target.value) || 0)}
                      className="w-full text-[11px] text-center border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1" />
                  </div>
                  <div className="text-center">
                    <span className="text-[8px] text-[#676664]/40 block">8D Price</span>
                    <input type="number" value={v.price_8_day || 0} onChange={e => updateVariation(idx, "price_8_day", parseFloat(e.target.value) || 0)}
                      className="w-full text-[11px] text-center border-0 border-b border-transparent hover:border-[#282828]/20 focus:border-[#9A7B4F] focus:outline-none bg-transparent py-1" />
                  </div>
                  <button onClick={() => removeVariation(idx)} className="text-[#676664]/20 hover:text-red-500 justify-self-center" data-testid={`remove-variation-${idx}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {variations.length === 0 && (
                <p className="text-[11px] text-[#676664]/40 text-center py-6">No variations. Add variations for different rental options (e.g. "With Dupatta", "Without Dupatta").</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#282828]/8 bg-[#faf9f5]">
          <div className="text-[11px] text-[#676664]/50">
            {product.images.length} image{product.images.length !== 1 ? 's' : ''} · {variations.length} variation{variations.length !== 1 ? 's' : ''}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="text-[11px]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px]" data-testid="save-new-product">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Create Product
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// Bulk Category Assignment Modal
function BulkCategoryModal({ isOpen, onClose, categories, onAssign, loading }) {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubs, setSelectedSubs] = useState([]);
  
  const parentCategories = categories.filter(c => !c.parent_id);
  const selectedParent = categories.find(c => c.id === selectedCategory);
  const availableSubs = selectedParent?.children || [];
  
  const handleSubmit = () => {
    onAssign(selectedCategory, selectedSubs);
  };
  
  const toggleSub = (subId) => {
    setSelectedSubs(prev => 
      prev.includes(subId) ? prev.filter(s => s !== subId) : [...prev, subId]
    );
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white z-50 w-full max-w-md p-6 shadow-xl" data-testid="bulk-category-modal">
        <h3 className="text-lg text-[#282828] font-light mb-4">Assign Category</h3>
        <p className="text-[12px] text-[#676664]/60 mb-4">Select a category to assign to all selected products</p>
        
        {/* Parent Category */}
        <div className="mb-4">
          <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Parent Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubs([]); }}
            className="w-full px-3 py-2 border border-[#282828]/10 text-[12px] bg-white focus:border-[#9A7B4F] focus:outline-none"
          >
            <option value="">— Select Category —</option>
            {parentCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        
        {/* Subcategories */}
        {availableSubs.length > 0 && (
          <div className="mb-4">
            <label className="text-[10px] text-[#676664]/60 uppercase block mb-2">Subcategories</label>
            <div className="flex flex-wrap gap-2">
              {availableSubs.map(sub => (
                <label 
                  key={sub.id} 
                  className={`flex items-center gap-1.5 px-3 py-1.5 border text-[11px] cursor-pointer transition-colors ${
                    selectedSubs.includes(sub.id) 
                      ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' 
                      : 'bg-white text-[#676664] border-[#282828]/10 hover:border-[#9A7B4F]/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSubs.includes(sub.id)}
                    onChange={() => toggleSub(sub.id)}
                    className="sr-only"
                  />
                  {sub.name}
                </label>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="text-[11px]" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !selectedCategory}
            className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Apply to Selected
          </Button>
        </div>
      </div>
    </>
  );
}

// Filter types
const FILTER_OPTIONS = [
  { value: "", label: "All Products" },
  { value: "out_of_stock", label: "Out of Stock" },
  { value: "hidden", label: "Hidden" },
  { value: "missing_price", label: "Missing Price" },
  { value: "missing_image", label: "Missing Image" },
  { value: "archived", label: "Archived" },
];

export default function CatalogueEditMode() {
  const [categories, setCategories] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Filters and search
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  
  // Lightweight product index for name/image resolution across pages
  const [productIndex, setProductIndex] = useState({});
  const [aiSorting, setAiSorting] = useState(false);
  
  // UI state
  const [expandedRows, setExpandedRows] = useState({});
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  const navigate = useNavigate();
  
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    fetchCategories();
    fetchEventTypes();
    fetchProductIndex();
  }, [navigate]);
  
  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedSubcategory, searchQuery, filterType, page, includeArchived, showAll]);
  
  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API}/settings/event-types?active_only=false`);
      setEventTypes(res.data.event_types || []);
    } catch { /* silent */ }
  };
  
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/settings/categories/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductIndex = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/outfits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const idx = {};
      (res.data || []).forEach(p => { idx[p.id] = { name: p.name, image: p.images?.[0] }; });
      setProductIndex(idx);
    } catch { /* silent */ }
  };
  
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedSubcategory) params.append("subcategory", selectedSubcategory);
      if (searchQuery) params.append("search", searchQuery);
      if (filterType) params.append("filter_type", filterType);
      if (includeArchived) params.append("include_archived", "true");
      if (!showAll) {
        params.append("page", page.toString());
        params.append("limit", "30");
      } else {
        params.append("page", "1");
        params.append("limit", "999");
      }
      
      const res = await axios.get(`${API}/outfits/catalogue?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data.products || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const handleToggleVisibility = async (productId, visible) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.patch(`${API}/outfits/${productId}/inline`, 
        { available: visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(products.map(p => p.id === productId ? { ...p, available: visible } : p));
      toast.success(visible ? "Product visible" : "Product hidden");
    } catch {
      toast.error("Failed to update visibility");
    }
  };
  
  const handleArchive = async (productId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.patch(`${API}/outfits/${productId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(products.map(p => p.id === productId ? { ...p, archived: res.data.archived } : p));
      toast.success(res.data.message);
    } catch {
      toast.error("Failed to archive product");
    }
  };
  
  const handleNewProduct = (newProduct) => {
    setProducts([newProduct, ...products]);
    setShowNewProductForm(false);
    setTotal(total + 1);
  };
  
  // Bulk selection handlers
  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };
  
  const selectAllProducts = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };
  
  const clearSelection = () => {
    setSelectedProducts(new Set());
  };
  
  // Bulk category assignment
  //
  // Modal hands us IDs (parent + sub IDs). We convert to NAMES before saving so
  // the canonical category contract (names) is preserved — same shape as
  // single-row `CategoryPanel` writes and the Collection page (`AdminOutfits`)
  // writer. See `CategoryPanel` header comment for the full contract.
  const handleBulkCategoryAssign = async (categoryId, subcategoryIds) => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("adminToken");
    const ids = Array.from(selectedProducts);

    // Resolve names from the category tree
    const parent = categories.find(c => c.id === categoryId);
    const parentName = parent?.name || "";
    const subNames = (subcategoryIds || [])
      .map(sid => (parent?.children || []).find(s => s.id === sid)?.name)
      .filter(Boolean);

    let successCount = 0;
    for (const id of ids) {
      try {
        await axios.patch(`${API}/outfits/${id}/inline`, {
          categories: parentName ? [parentName] : [],
          subcategories: subNames,
          // Mirror first sub-name into legacy `category` singular for back-compat
          category: subNames[0] || "",
        }, { headers: { Authorization: `Bearer ${token}` } });
        successCount++;
      } catch (err) {
        console.error(`Failed to update ${id}`, err);
      }
    }
    
    setBulkActionLoading(false);
    setShowBulkCategoryModal(false);
    
    if (successCount > 0) {
      toast.success(`Updated ${successCount} products`);
      fetchProducts(); // Refresh to show updated categories
      clearSelection();
    } else {
      toast.error("Failed to update products");
    }
  };
  
  // Bulk visibility toggle
  const handleBulkVisibility = async (visible) => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("adminToken");
    const ids = Array.from(selectedProducts);
    
    let successCount = 0;
    for (const id of ids) {
      try {
        await axios.patch(`${API}/outfits/${id}/inline`, { available: visible }, { headers: { Authorization: `Bearer ${token}` } });
        successCount++;
      } catch (err) {
        console.error(`Failed to update ${id}`, err);
      }
    }
    
    setBulkActionLoading(false);
    
    if (successCount > 0) {
      toast.success(`${visible ? 'Showed' : 'Hid'} ${successCount} products`);
      setProducts(products.map(p => selectedProducts.has(p.id) ? { ...p, available: visible } : p));
      clearSelection();
    }
  };
  
  // Bulk archive
  const handleBulkArchive = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("adminToken");
    const ids = Array.from(selectedProducts);
    
    let successCount = 0;
    for (const id of ids) {
      try {
        await axios.patch(`${API}/outfits/${id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
        successCount++;
      } catch (err) {
        console.error(`Failed to archive ${id}`, err);
      }
    }
    
    setBulkActionLoading(false);
    
    if (successCount > 0) {
      toast.success(`Archived ${successCount} products`);
      fetchProducts();
      clearSelection();
    }
  };

  // Bulk CTL toggle
  const handleBulkCTLToggle = async (enable) => {
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const ids = Array.from(selectedProducts);
      await axios.patch(`${API}/outfits/bulk-complete-the-look`, {
        outfit_ids: ids,
        show_complete_the_look: enable
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${enable ? 'Enabled' : 'Disabled'} CTL for ${ids.length} products`);
      setProducts(products.map(p => selectedProducts.has(p.id) ? { ...p, show_complete_the_look: enable } : p));
      clearSelection();
    } catch { toast.error("Failed to update CTL visibility"); }
    finally { setBulkActionLoading(false); }
  };

  const handleAiMerchandising = async () => {
    if (!selectedCategory) {
      toast.error("Select a category first to run AI merchandising");
      return;
    }
    setAiSorting(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.post(`${API}/ai/admin/merchandising/${encodeURIComponent(selectedCategory)}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      toast.success(`AI reordered ${res.data.count} products for optimal display`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI merchandising failed");
    } finally {
      setAiSorting(false);
    }
  };
  
  const toggleExpand = (productId) => {
    setExpandedRows(prev => ({ ...prev, [productId]: !prev[productId] }));
  };
  
  // Get subcategories for selected category
  const selectedCategoryData = categories.find(c => c.id === selectedCategory || c.slug === selectedCategory);
  const subcategories = selectedCategoryData?.children || [];
  
  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setPage(1);
    }, 400),
    []
  );
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F0]">
        <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 lg:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/admin/categories" className="text-[#676664]/40 hover:text-[#676664]">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-4 h-4 text-[#9A7B4F]" />
              <h1 className="text-lg text-[#282828] font-light">Catalogue Edit Mode</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-[#676664]/60">{total} products</span>
              <Button
                onClick={handleAiMerchandising}
                disabled={aiSorting}
                variant="outline"
                className="text-[11px] tracking-[0.08em] uppercase border-[#9A7B4F]/30 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
                data-testid="ai-merchandising-btn"
              >
                {aiSorting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {aiSorting ? "AI Sorting..." : "AI Merchandising"}
              </Button>
            <Button 
              onClick={() => setShowNewProductForm(true)} 
              className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px] tracking-[0.08em] uppercase"
              data-testid="add-product-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Product
            </Button>
          </div>
        </div>
        
        {/* Filters Bar */}
        <div className="bg-white border border-[#282828]/8 px-4 py-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
              <input
                type="text"
                placeholder="Search name, SKU, description..."
                onChange={(e) => debouncedSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-[#282828]/10 text-[12px] focus:border-[#9A7B4F] focus:outline-none"
                data-testid="search-input"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(""); setPage(1); }}
              className="px-3 py-2 border border-[#282828]/10 text-[12px] bg-white focus:border-[#9A7B4F] focus:outline-none"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            
            {/* Subcategory Filter */}
            {subcategories.length > 0 && (
              <select
                value={selectedSubcategory}
                onChange={(e) => { setSelectedSubcategory(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-[#282828]/10 text-[12px] bg-white focus:border-[#9A7B4F] focus:outline-none"
                data-testid="subcategory-filter"
              >
                <option value="">All Subcategories</option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            )}
            
            {/* Status Filter */}
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-[#282828]/10 text-[12px] bg-white focus:border-[#9A7B4F] focus:outline-none"
              data-testid="status-filter"
            >
              {FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            
            {/* Include Archived Toggle */}
            <label className="flex items-center gap-2 text-[11px] text-[#676664] cursor-pointer">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }}
                className="rounded border-[#282828]/20"
              />
              Include Archived
            </label>
          </div>
        </div>
        
        {/* Bulk Action Bar - appears when products are selected */}
        {selectedProducts.size > 0 && (
          <div className="bg-[#9A7B4F]/10 border border-[#9A7B4F]/20 px-4 py-3 mb-4 flex items-center justify-between" data-testid="bulk-action-bar">
            <div className="flex items-center gap-3">
              <span className="text-[12px] text-[#282828] font-medium">{selectedProducts.size} selected</span>
              <button onClick={clearSelection} className="text-[11px] text-[#676664] hover:text-[#282828] underline">Clear</button>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowBulkCategoryModal(true)}
                disabled={bulkActionLoading}
                className="text-[11px] border-[#9A7B4F] text-[#9A7B4F]"
                data-testid="bulk-assign-category"
              >
                Assign Category
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkVisibility(true)}
                disabled={bulkActionLoading}
                className="text-[11px]"
              >
                <Eye className="w-3.5 h-3.5 mr-1" /> Show All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkVisibility(false)}
                disabled={bulkActionLoading}
                className="text-[11px]"
              >
                <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkArchive}
                disabled={bulkActionLoading}
                className="text-[11px] text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                <Archive className="w-3.5 h-3.5 mr-1" /> Archive All
              </Button>
              <div className="w-px h-5 bg-[#282828]/10" />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkCTLToggle(true)}
                disabled={bulkActionLoading}
                className="text-[11px] text-[#9A7B4F] border-[#9A7B4F]/30"
                data-testid="bulk-ctl-enable"
              >
                Enable CTL
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleBulkCTLToggle(false)}
                disabled={bulkActionLoading}
                className="text-[11px] text-[#676664] border-[#282828]/15"
                data-testid="bulk-ctl-disable"
              >
                Disable CTL
              </Button>
            </div>
          </div>
        )}
        
        {/* Products Table */}
        <div className="bg-white border border-[#282828]/8 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[30px_40px_70px_1fr_80px_80px_80px_80px_60px_70px_100px] gap-2 items-center px-3 py-2.5 bg-[#f7f5f4] border-b border-[#282828]/8 text-[10px] uppercase tracking-wider text-[#676664]/60 font-medium">
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={products.length > 0 && selectedProducts.size === products.length}
                onChange={selectAllProducts}
                className="rounded border-[#282828]/20 w-4 h-4"
                data-testid="select-all-checkbox"
              />
            </div>
            <div />
            <div>Image</div>
            <div>Product</div>
            <div className="text-center">4D Price</div>
            <div className="text-center">8D Price</div>
            <div className="text-center">Bond</div>
            <div className="text-center">RRP</div>
            <div className="text-center">Stock</div>
            <div className="text-center">Vars</div>
            <div className="text-right pr-2">Actions</div>
          </div>
          
          {/* New Product Form is now rendered as a modal outside the table */}
          
          {/* Loading State */}
          {loadingProducts && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" />
            </div>
          )}
          
          {/* Products List */}
          {!loadingProducts && products.length > 0 && (
            products.map(product => (
              <ProductRow
                key={product.id}
                product={product}
                categories={categories}
                eventTypes={eventTypes}
                allProducts={products}
                productIndex={productIndex}
                selected={selectedProducts.has(product.id)}
                onSelect={toggleSelectProduct}
                onToggleVisibility={handleToggleVisibility}
                onArchive={handleArchive}
                onDelete={() => {}}
                onUpdate={() => {}}
                expanded={expandedRows[product.id]}
                onToggleExpand={toggleExpand}
                onDuplicate={async (prod) => {
                  try {
                    const token = localStorage.getItem("adminToken");
                    const { id, created_at, last_edited_at, sku, ...rest } = prod;
                    const payload = { ...rest, name: `${prod.name} (Copy)`, images: prod.images || [], videos: prod.videos || [] };
                    await axios.post(`${API}/outfits`, payload, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success(`Duplicated "${prod.name}"`);
                    fetchProducts();
                  } catch { toast.error("Failed to duplicate"); }
                }}
              />
            ))
          )}
          
          {/* Empty State */}
          {!loadingProducts && products.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-12 h-12 text-[#676664]/15 mx-auto mb-4" strokeWidth={1} />
              <p className="text-[#676664]/50 text-sm font-light mb-2">No products found</p>
              <p className="text-[#676664]/30 text-[11px]">
                {searchQuery || filterType ? "Try adjusting your filters" : "Add your first product to get started"}
              </p>
              {!searchQuery && !filterType && (
                <Button 
                  onClick={() => setShowNewProductForm(true)}
                  className="mt-4 bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px]"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Product
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 px-1">
          <button
            onClick={() => { setShowAll(!showAll); setPage(1); }}
            className="text-[11px] text-[#9A7B4F] hover:text-[#8a6b3f] underline underline-offset-2"
            data-testid="show-all-toggle"
          >
            {showAll ? `Show paginated (30/page)` : `Show all ${total} items`}
          </button>
          {!showAll && totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="text-[11px]"
              >
                Previous
              </Button>
              <span className="text-[12px] text-[#676664]/60">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
                className="text-[11px]"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Bulk Category Assignment Modal */}
      <BulkCategoryModal
        isOpen={showBulkCategoryModal}
        onClose={() => setShowBulkCategoryModal(false)}
        categories={categories}
        onAssign={handleBulkCategoryAssign}
        loading={bulkActionLoading}
      />
      
      {/* New Product Modal */}
      {showNewProductForm && (
        <NewProductForm
          categoryId={selectedCategory}
          subcategoryId={selectedSubcategory}
          categories={categories.flatMap(c => [c, ...(c.children || [])])}
          onSave={handleNewProduct}
          onCancel={() => setShowNewProductForm(false)}
        />
      )}
    </div>
  );
}
